import Core            from '@/core/Core';
import BudgetPrice     from '@/domains/BudgetPrices/BudgetPrice';
import BudgetPricesDAO from '@/domains/BudgetPrices/BudgetPricesDAO';
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

/**
 * Сервис домена бюджетных цен.
 */
class BudgetPricesService {

  /**
   * Загрузить одну бюджетную цену по productId.
   */
  static async get(productId: number): Promise<BudgetPrice | null> {
    const response = await BudgetPricesDAO.get(productId);
    return Core.utils.hydrate(response.data, BudgetPrice.fromServer);
  }

  /**
   * Загрузить список бюджетных цен.
   * @param productIds — один или несколько productId; без параметра вернёт все
   */
  static async getList(productIds?: number | number[]): Promise<BudgetPrice[]> {
    const response = await BudgetPricesDAO.getList({ productIds });

    return Core.utils.hydrateArray(response.data, BudgetPrice.fromServer);
  }

  static async getListPaginated(
    params?: Parameters<typeof BudgetPricesDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort
    }
  ): Promise<{ data: BudgetPrice[], pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await BudgetPricesDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, BudgetPrice.fromServer),
      pagination: response.meta.pagination
    };
  }

  static async upsert(budgetPrice: BudgetPrice): Promise<boolean> {
    const response = await BudgetPricesDAO.upsert(budgetPrice);

    if (response.ok) {
      Core.showNotification.success('Бюджетная цена обновлена');
    }

    return response.ok;
  }

  /**
   * Удалить бюджетную цену по id.
   */
  static async delete(id: number): Promise<boolean> {
    const response = await BudgetPricesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Бюджетная цена удалена');
    }

    return response.ok;
  }
  /* Удаление бюджетной цены с подтверждением */
  static async deleteWithConfirm(budgetPrice: BudgetPrice): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление бюджетной цены ' + budgetPrice.product.name + '?');

    if (confirmed) {
      return await this.delete(budgetPrice.product.id);
    }
    else {
      return false;
    }
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (budgetPrice: BudgetPrice) => { return Core.dialogs.BudgetPriceCard.showAsPromise({ productId: budgetPrice.product.id }); }
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (budgetPrice: BudgetPrice) => { return this.deleteWithConfirm(budgetPrice); }
      )
    ]
  }
}

export default BudgetPricesService;
